import { cloneDeep } from 'lodash-es'
import { Command } from '@/plugins/cad/module/core/Command'
import { EntityEditHandler, type ExtendEntityParams } from '../edit/EntityEditHandler'
import type { CADViewManager } from '../manager/CADViewManager'
import { EntityType } from '../model/common/constant'
import type { ArcEntity, IEntity, PolylineEntity, RayEntity, SplineEntity } from '../model/entity'

/**
 * 延伸实体命令（原地修改端点，支持撤销）。
 * 采用快照方式保存原始几何数据，undo 时还原。
 */
export class ExtendEntityCommand extends Command {
  // 存储每个被修改实体的原始数据快照，支持多实体延伸在同一命令中执行
  private data: {
    entity: IEntity
    originData: Record<string, any>
    params: ExtendEntityParams
  }[] = []

  constructor(private readonly viewManager: CADViewManager) {
    super()
  }

  /** 实际修改在 addEntity 中完成，这里为空实现 */
  execute(): void {
    // 已在addEntity中处理实体修改，这里不再执行具体操作
  }

  /** 撤销：按快照还原所有被延伸的实体 */
  undo(): void {
    // 逐个还原被修改的实体
    const entities: IEntity[] = []
    for (const { entity, originData } of this.data) {
      // 根据实体类型还原对应的字段
      Object.assign(entity, cloneDeep(originData))
      entities.push(entity)
    }
    this.viewManager.updateEntities(entities, { updateGeometry: true })
  }

  /** 重做：按记录的参数再次执行延伸 */
  redo() {
    // 逐个重新执行延伸修改
    for (const { entity, params } of this.data) {
      if (params) {
        EntityEditHandler.extend(entity, params)
        this.viewManager.updateEntity(entity, { updateGeometry: true })
      }
    }
  }

  /** 分步撤销：还原最近一次延伸的实体 */
  undoStep() {
    if (!this.canUndoStep()) return
    const { entity, originData } = this.data.pop()!
    // 根据实体类型还原对应的字段
    Object.assign(entity, cloneDeep(originData))
    this.viewManager.updateEntity(entity, { updateGeometry: true })
  }

  /**
   * 是否支持分步撤销。
   *
   * @returns 存在已记录的延伸操作时返回 true
   */
  canUndoStep(): boolean {
    return this.data.length > 0
  }

  /**
   * 是否允许提交命令。
   *
   * @returns 至少处理过一次延伸时返回 true
   */
  canCommit(): boolean {
    return this.data.length > 0
  }

  /**
   * 追加一次延伸操作并立即执行，记录原始几何快照以支持撤销。
   *
   * @param entity - 待延伸的实体
   * @param params - 延伸参数，例如目标端点及方向
   */
  addEntity(entity: IEntity, params: ExtendEntityParams): void {
    // 在 execute 之前快照需要修改的字段
    const originData = this.getOriginalEntityData(entity)
    if (!originData) {
      return
    }

    // 记录实体和对应的原始数据快照
    this.data.push({ entity, originData, params })
    // 执行延伸修改
    EntityEditHandler.extend(entity, params)
    // 更新视图
    this.viewManager.updateEntity(entity, { updateGeometry: true })
  }

  // 记录原始数据快照
  private getOriginalEntityData(entity: IEntity) {
    let snapshot: Record<string, any> | undefined
    switch (entity.type) {
      case EntityType.Polyline:
        snapshot = {
          vertices: cloneDeep((entity as PolylineEntity).vertices),
        }
        break
      case EntityType.Arc: {
        const arc = entity as ArcEntity
        snapshot = {
          startAngle: arc.startAngle,
          endAngle: arc.endAngle,
        }
        break
      }
      case EntityType.Ray:
        snapshot = {
          startPoint: cloneDeep((entity as RayEntity).startPoint),
        }
        break
      case EntityType.Spline: {
        const spline = entity as SplineEntity
        snapshot = {
          controlPoints: cloneDeep(spline.controlPoints),
          knots: cloneDeep(spline.knots),
          degree: spline.degree,
          fitPoints: cloneDeep(spline.fitPoints),
        }
        break
      }
      // 其他类型根据需要添加
    }
    return snapshot
  }
}
